import { TableCell, Typography } from '@material-ui/core'
import React, { useCallback, EventHandler, useState, ChangeEvent, KeyboardEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { StringParam, useQueryParams } from 'use-query-params'

import { CreatePartnerDialog } from '../createPartnerDialog/CreatePartnerDialog'

import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { ErrorView, ClickableTableRow, Table, PercentButton } from '@percent/admin-dashboard/common/components'
import { useDidMountEffect, useFilterValue, usePermissions, useQueryList } from '@percent/admin-dashboard/common/hooks'
import { ReactComponent as DropdownSVG } from '@percent/admin-dashboard/common/assets/images/dropdown.svg'
import style from './PartnerListTable.module.scss'
import { Flex, SearchInput } from '@percent/lemonade'
import { handleClickAsLink } from '@percent/admin-dashboard/common/utility/handleClickAsLink'

export function PartnerListTable() {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const { adminService } = useServices()
  const { userHasPermissions } = usePermissions()

  const [queryParams, setQueryParams] = useQueryParams({
    query: StringParam
  })

  const [searchValue, setSearchValue] = useFilterValue<string>(queryParams.query ?? '', [queryParams.query])

  const [{ totalResults, errorMessageOrNull, dataOrNull, isLoading }, { query, nextPage, previousPage, refresh }] =
    useQueryList(adminService.queries.getPartnerList, {
      query: queryParams.query ?? ''
    })

  const { t } = useTranslation()

  const handleCreatePartnerClick = () => {
    setOpen(true)
  }

  const handleClose = () => setOpen(false)

  const handleClearValue = () => {
    setQueryParams({ query: '' })
  }

  const handleKeyChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value)
  }

  const handleKeyPress: EventHandler<KeyboardEvent<HTMLInputElement>> = useCallback(
    event => {
      if (event.key === 'Enter') {
        setQueryParams({
          query: (event.target as HTMLInputElement).value
        })
      }

      if (event.key === 'Escape') {
        setQueryParams({ query: '' })
      }

      return null
    },
    [setQueryParams]
  )

  useDidMountEffect(() => query(queryParams), [queryParams, query])

  if (errorMessageOrNull) {
    return <ErrorView errorMessage={errorMessageOrNull} />
  }

  const permission = (action: string) => userHasPermissions(`partners:${action}`)

  const header = (
    <Flex direction="column" gap={24} className={style.header}>
      <>
        <Typography variant="h6">{t('typography.partners')}</Typography>
        {permission('can_create') && (
          <PercentButton
            className={style.addPartnerButton}
            title={`+ ${t('button.addAPartner')}`}
            onClick={handleCreatePartnerClick}
            alignment="alignRight"
          />
        )}
        {open && <CreatePartnerDialog onClose={handleClose} open={open} refresh={refresh} />}
      </>
      <SearchInput
        value={searchValue ?? ''}
        handleClearValue={handleClearValue}
        onKeyDown={handleKeyPress}
        onChange={handleKeyChange}
        placeholder={t('table.adminPartnerSearchPlaceholder')}
      />
    </Flex>
  )

  const emptyTableActions = permission('can_create') ? (
    <PercentButton
      className={style.actionsAddPartnerButton}
      title={`+ ${t('button.addAPartner')}`}
      onClick={handleCreatePartnerClick}
      alignment="alignRight"
    />
  ) : null

  return (
    <Table
      isLoading={isLoading}
      totalResults={totalResults}
      data={dataOrNull}
      previousPage={previousPage}
      nextPage={nextPage}
      value={0}
      handleChange={() => {}}
      handleRequestSort={() => {}}
      orderBy=""
      title={header}
      columns={[{ id: 'name', isSortable: false, props: { width: '300px' } }]}
      emptyTableText={t('table.emptyPartners')}
      emptyTableImageType="partners"
      emptyTableChildren={emptyTableActions}
    >
      {dataOrNull ? (
        <>
          {dataOrNull.map(({ id, name }) => (
            <ClickableTableRow key={`partner-list-${id}`} onClick={handleClickAsLink(`/partners/${id}`, navigate)}>
              <TableCell className={style.row}>{name}</TableCell>
              <TableCell />
              <TableCell />
              <TableCell className={style.row} align="right" style={{ padding: '16px 0' }}>
                <DropdownSVG className={style.arrow} />
              </TableCell>
            </ClickableTableRow>
          ))}
        </>
      ) : null}
    </Table>
  )
}
